import { useAccount } from '@/domain/hooks/useAccount'
import { useIsUsingBigBlocks } from '@/domain/hooks/useIsUsingBigBlocks'
import * as hl from '@nktkas/hyperliquid'
import { useCallback, useState } from 'react'
import { zeroAddress } from 'viem'
import { generatePrivateKey, privateKeyToAccount } from 'viem/accounts'
import { useWalletClient } from 'wagmi'
import { captureError } from '../../../../instrumentation-client'

const transport = new hl.HttpTransport()
const AGENT_NAME = 'HypurrFiBlocks'

// Retry configuration for handling "pending agent removal" race condition
const MAX_AGENT_APPROVAL_RETRIES = 5
const RETRY_DELAY_MS = 1500

/**
 * Helper to retry an async operation with delay when hitting specific errors
 */
async function retryWithBackoff<T>(
  operation: () => Promise<T>,
  shouldRetry: (error: unknown) => boolean,
  maxRetries: number = MAX_AGENT_APPROVAL_RETRIES,
  delayMs: number = RETRY_DELAY_MS,
): Promise<T> {
  let lastError: unknown
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await operation()
    } catch (err) {
      lastError = err
      if (shouldRetry(err) && attempt < maxRetries - 1) {
        await new Promise((resolve) => setTimeout(resolve, delayMs))
        continue
      }
      throw err
    }
  }
  throw lastError
}

/**
 * Check if an error is due to pending agent removal (race condition)
 */
function isPendingAgentRemovalError(err: unknown): boolean {
  return err instanceof Error && err.message.toLowerCase().includes('pending agent removal')
}

export type BigBlocksToggleStep = 'approve-agent' | 'modify-settings' | 'revoke-agent'

export interface UseBigBlocksToggleResult {
  /** Toggle big blocks on or off */
  setIsUsingBigBlocks: (usingBigBlocks: boolean) => Promise<boolean>
  /** Whether an operation is in progress */
  isLoading: boolean
  /** Current step progress (for detailed UI) */
  currentStep: BigBlocksToggleStep | null
  /** Error message if operation failed */
  error: string | null
  /** Reset error state */
  resetError: () => void
}

class BlockSizeError extends Error {
  details?: unknown
  constructor(message: string, details?: unknown) {
    super(message)
    this.name = 'BlockSizeError'
    this.details = details
  }
}

/**
 * Hook for toggling big blocks setting on HyperEVM.
 *
 * This hook handles the full flow:
 * 1. Approve a temporary agent
 * 2. Use agent to modify EVM settings
 * 3. Revoke the agent
 *
 * @returns Functions and state for managing big blocks toggle
 */
export function useBigBlocksToggle(): UseBigBlocksToggleResult {
  const account = useAccount()
  const { data: walletClient } = useWalletClient()
  const { refetch: refetchBigBlocks } = useIsUsingBigBlocks()

  const [isLoading, setIsLoading] = useState(false)
  const [currentStep, setCurrentStep] = useState<BigBlocksToggleStep | null>(null)
  const [error, setError] = useState<string | null>(null)

  const resetError = useCallback(() => {
    setError(null)
  }, [])

  const setIsUsingBigBlocks = useCallback(
    async (usingBigBlocks: boolean): Promise<boolean> => {
      try {
        setIsLoading(true)
        setError(null)
        setCurrentStep('approve-agent')

        if (!account || !walletClient) {
          throw new Error('No wallet found')
        }

        // Step 1: Create and approve temporary agent
        const hlClient = new hl.ExchangeClient({
          wallet: walletClient,
          transport,
        })

        const agentKey = generatePrivateKey()
        const agentAccount = privateKeyToAccount(agentKey)

        // Use retry logic to handle "pending agent removal" race condition
        // This can happen when switching back to small blocks right after enabling big blocks
        await retryWithBackoff(
          () =>
            hlClient.approveAgent({
              agentAddress: agentAccount.address,
              agentName: AGENT_NAME,
            }),
          isPendingAgentRemovalError,
        )

        // Step 2: Use agent to modify EVM settings
        setCurrentStep('modify-settings')

        const hlAgentClient = new hl.ExchangeClient({
          wallet: agentAccount,
          transport,
        })
        const response = await hlAgentClient.evmUserModify({ usingBigBlocks })

        if (response.status !== 'ok') {
          throw new BlockSizeError('Failed to set block size', { response })
        }

        // Step 3: Revoke the agent
        setCurrentStep('revoke-agent')

        try {
          // Use retry logic here too - can hit "pending agent removal" if called rapidly
          await retryWithBackoff(
            () =>
              hlClient.approveAgent({
                agentAddress: zeroAddress,
                agentName: AGENT_NAME,
              }),
            isPendingAgentRemovalError,
          )
        } catch (revokeErr) {
          // Ignore "Extra agent does not exist" error - the agent was already revoked
          if (!(revokeErr instanceof Error && revokeErr.message.includes('does not exist'))) {
            throw revokeErr
          }
        }

        // Refresh the big blocks status
        await refetchBigBlocks()

        return true
      } catch (err) {
        if (err instanceof Error) {
          captureError(err, err instanceof BlockSizeError ? (err.details as Record<string, unknown>) : undefined)

          if (!err.message.includes('User rejected') && !err.message.includes('User disapproved')) {
            console.error(err)
            const errorMessage = err.message || 'Failed to set block size'
            setError(errorMessage)
            // Throw error so callers can catch it directly (avoids stale closure issues)
            throw new Error(errorMessage)
          }
        }
        // User rejection - not an error state, just return false
        return false
      } finally {
        setIsLoading(false)
        setCurrentStep(null)
      }
    },
    [account, walletClient, refetchBigBlocks],
  )

  return {
    setIsUsingBigBlocks,
    isLoading,
    currentStep,
    error,
    resetError,
  }
}
